import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Star, Sparkles, Target, Crown } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GoalSpotlight = ({ user }) => {
  const [spotlight, setSpotlight] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSpotlight();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSpotlight = async () => {
    try {
      const res = await fetch(`${API_URL}/api/goals/spotlight`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        setSpotlight(await res.json());
      }
    } catch (error) {
      console.error('Error:', error);
    }
    setLoading(false);
  };

  if (loading || !spotlight || spotlight.spotlight?.length === 0) {
    return null;
  }

  const getRankEmoji = (index) => {
    switch(index) {
      case 0: return '🥇';
      case 1: return '🥈';
      case 2: return '🥉';
      default: return '⭐';
    }
  };

  const getRankColor = (index) => {
    switch(index) {
      case 0: return 'from-yellow-400 to-amber-500';
      case 1: return 'from-gray-300 to-gray-400';
      case 2: return 'from-orange-400 to-orange-500';
      default: return 'from-blue-400 to-blue-500';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-6"
    >
      <Card className="overflow-hidden rounded-3xl bg-gradient-to-br from-purple-600 via-indigo-600 to-blue-600 text-white">
        {/* Header */}
        <div className="p-4 pb-2">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur">
              <Trophy className="w-7 h-7 text-yellow-300" />
            </div>
            <div>
              <h3 className="font-heading text-xl font-bold flex items-center gap-2">
                Goal Achievers of the Week
                <Sparkles className="w-5 h-5 text-yellow-300" />
              </h3>
              <p className="text-sm text-white/80">
                {spotlight.total_goals_completed_this_week} goals completed this week!
              </p>
            </div>
          </div>
        </div>

        {/* Spotlight List */}
        <div className="p-4 pt-2 space-y-3">
          {spotlight.spotlight.map((achiever, index) => {
            const isCurrentUser = achiever.user_id === user?.id;
            
            return (
              <motion.div
                key={achiever.user_id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`flex items-center gap-3 p-3 rounded-2xl ${
                  isCurrentUser 
                    ? 'bg-yellow-400/30 ring-2 ring-yellow-400' 
                    : 'bg-white/10'
                } backdrop-blur`}
              >
                {/* Rank */}
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${getRankColor(index)} flex items-center justify-center text-xl shadow-lg`}>
                  {getRankEmoji(index)}
                </div>

                {/* Avatar */}
                <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center text-2xl">
                  {achiever.user_avatar}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-bold truncate">
                      {achiever.user_name}
                      {isCurrentUser && <span className="text-yellow-300"> (You!)</span>}
                    </span>
                    {index === 0 && <Crown className="w-4 h-4 text-yellow-300" />}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-white/80">
                    <Target className="w-3 h-3" />
                    <span>{achiever.count} goal{achiever.count > 1 ? 's' : ''} completed</span>
                    {achiever.team_name && (
                      <Badge className="bg-white/20 text-white text-xs">
                        {achiever.team_name}
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Points */}
                <div className="text-right">
                  <p className="font-bold text-lg">{achiever.total_points}</p>
                  <p className="text-xs text-white/70">pts</p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Footer Message */}
        <div className="px-4 pb-4">
          <div className="bg-white/10 rounded-xl p-3 text-center text-sm">
            <Star className="w-4 h-4 inline-block mr-1 text-yellow-300" />
            Complete your goals to get featured here next week!
          </div>
        </div>
      </Card>
    </motion.div>
  );
};

export default GoalSpotlight;
